import { Box, type BoxProps } from '@mui/material';
import CodeMirror, {
  EditorSelection,
  EditorView,
  type Extension,
  type ReactCodeMirrorRef,
} from '@uiw/react-codemirror';
import { type CSSProperties, memo, useMemo, useRef } from 'react';

import { isDefined } from 'common';

import { defaultCodeMirrorProps } from './defaultProps';
import { type UseSelectedNodeFieldProps } from '../useSelectedNodeField';

interface PropDrivenBlockValueTemplateInnerFieldProps {
  fieldId: string;
  'aria-label': string;
  wrapperSx: BoxProps['sx'];
  codeMirrorStyles: CSSProperties;
  field: UseSelectedNodeFieldProps<string>;
  isEditable: boolean;
  value: string | null | undefined;
  onChange: (value: string) => Promise<void>;
  autoFocus?: boolean;
  placeholder?: string;
  isMultiline?: boolean;
  minHeight?: string;
  maxHeight?: string;
  extensions: Extension[];
  onUpdate?: (value: string) => void;
}

/**
 * This is a pure component driven by props.
 * You must ensure that all properties passed to this component are stable, since unstable props can
 * cause significant performance issues.
 * You must test performance with a block that has 100s of fields.
 *
 * IMPORTANT:
 * The main risk with memoizing a component is that all props must be memoized or be stable as well.
 * Memoization uses shallow compare, and if a prop is not stable, it will cause re-evaluation of the component rendering.
 *
 * Of note:
 * 1. Primitive values, such as numbers, booleans, and strings are stable, and do not need memoization.
 * 2. Objects and functions must be memoized when fed to a memoized consumer
 */
function PropDrivenBlockValueTemplateInnerField(
  props: PropDrivenBlockValueTemplateInnerFieldProps
) {
  const {
    'aria-label': ariaLabel,
    wrapperSx,
    codeMirrorStyles,
    fieldId,
    field,
    isEditable,
    value,
    onChange,
    autoFocus,
    extensions,
    isMultiline,
    minHeight,
    maxHeight,
    placeholder,
  } = props;
  const codeMirrorRef = useRef<ReactCodeMirrorRef>(null);
  const codeMirrorExtensions = useMemo(() => {
    return isMultiline ? [...extensions, EditorView.lineWrapping] : extensions;
  }, [isMultiline, extensions]);

  return (
    <Box sx={wrapperSx}>
      <CodeMirror
        id={fieldId}
        ref={codeMirrorRef}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        extensions={codeMirrorExtensions}
        basicSetup={{
          lineNumbers: false,
          foldGutter: false,
          highlightActiveLine: isMultiline,
          closeBrackets: false,
        }}
        selection={EditorSelection.cursor(value?.length ?? 0)}
        {...field}
        value={
          isDefined(value)
            ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
              String(value)
            : undefined
        }
        editable={isEditable}
        onChange={onChange}
        {...defaultCodeMirrorProps}
        minHeight={minHeight}
        maxHeight={maxHeight ?? '40vh'}
        placeholder={placeholder ?? ''}
        style={codeMirrorStyles}
      />
    </Box>
  );
}
export const MemoizedBlockValueTemplateInnerField = memo(PropDrivenBlockValueTemplateInnerField);
MemoizedBlockValueTemplateInnerField.displayName = 'MemoizedBlockValueTemplateInnerField';
